import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { AuthContext } from '../../context/auth-context';
import EmployeesAPI from '../../../services/api/admin/employees-api';

export const EmployeeCsvUploadModal = ({ open, onClose, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState(null);
  const { token } = useContext(AuthContext);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
      setUploadResults(null);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      const uploadResult = await EmployeesAPI.uploadCsv(formData, token);

      setUploadResults(uploadResult);

      // Call the callback to refresh the employees list
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResults(null);
    setError(null);
    onClose();
  };

  const getCsvTemplate = () => {
    const headers = [
      'firstname',
      'lastname',
      'coins_empl',
      'home_union',
      'employment_status_id',
    ];

    const sampleData = ['John', 'Doe', 'COINS001', 'E124', 'active'];

    const csvContent = [headers, sampleData]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'employees_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CloudUploadIcon />
          Upload CSV to Add Employees
        </Box>
      </DialogTitle>

      <DialogContent>
        {!uploadResults ? (
          <Box>
            <Typography variant="body1" paragraph>
              Upload a CSV file to add new employees to the system.
            </Typography>

            <Box mb={2}>
              <Button
                variant="outlined"
                onClick={getCsvTemplate}
                sx={{ mb: 2 }}
              >
                Download CSV Template
              </Button>
            </Box>

            <Box mb={2}>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="employee-csv-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="employee-csv-file-input">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  Select CSV File
                </Button>
              </label>
              {file && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: 'text.secondary' }}
                >
                  Selected: {file.name}
                </Typography>
              )}
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Supported columns:</strong> firstname, lastname,
                coins_empl, home_union, employment_status_id
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Results
            </Typography>

            <Box display="flex" gap={2} mb={2}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${uploadResults.successCount} Employees Added`}
                color="success"
              />
              {uploadResults.errorCount > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${uploadResults.errorCount} Errors`}
                  color="error"
                />
              )}
            </Box>

            {uploadResults.errors.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Errors:
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadResults.errors.map((error, index) => (
                        <TableRow key={index}>
                          <TableCell>{error.row}</TableCell>
                          <TableCell>{error.name || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error">
                              {error.error}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {uploadResults.success.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Successfully Added Employees:
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadResults.success.map((success, index) => (
                        <TableRow key={index}>
                          <TableCell>{success.row}</TableCell>
                          <TableCell>
                            {success.employee.firstname}{' '}
                            {success.employee.lastname}
                          </TableCell>
                          <TableCell>
                            {success.employee.email || 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!uploadResults ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!file || uploading}
              startIcon={
                uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />
              }
            >
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
